import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { User2, Building2, DollarSign, Calendar, ToggleLeft } from 'lucide-react';
import type { Employee } from '../types';

interface EmployeeFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  initialData?: Employee;
}

const defaultFormData: Omit<Employee, 'id'> = {
  firstName: '',
  lastName: '',
  email: '',
  position: '',
  department: '',
  salary: 0,
  paymentType: 'salary',
  startDate: new Date().toISOString().split('T')[0],
  taxStatus: 'W2',
  state: '',
  stateRate: 0,
  insuranceRate: 0,
  retirementRate: 0,
  federalTaxRate: 22,
  bonus: 0,
  isActive: true
};

const states = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

const EmployeeForm: React.FC<EmployeeFormProps> = ({ onSuccess, onCancel, initialData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Employee, 'id'>>({
    ...defaultFormData,
    ...initialData
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      if (formData.salary <= 0) {
        throw new Error('Salary must be greater than 0');
      }

      const federalTaxRate = Number(formData.federalTaxRate);
      if (isNaN(federalTaxRate) || federalTaxRate < 0) {
        throw new Error('Federal tax rate must be a non-negative number');
      }

      // Prevent using the default admin email
      if (!initialData && formData.email === 'rollins.jeremy@gmail.com') {
        throw new Error('This email address is reserved for system use');
      }

      const employeeData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        position: formData.position,
        department: formData.department,
        payment_type: formData.paymentType,
        salary: formData.salary,
        start_date: formData.startDate,
        tax_status: formData.taxStatus,
        state: formData.state || null,
        state_rate: formData.stateRate,
        insurance_rate: formData.insuranceRate,
        retirement_rate: formData.retirementRate,
        federal_tax_rate: federalTaxRate,
        bonus: formData.bonus,
        is_active: formData.isActive,
        is_admin: false // Always set new employees as non-admin
      };

      let result;
      if (initialData) {
        // When updating, don't modify admin status
        const { is_admin, ...updateData } = employeeData;
        result = await supabase
          .from('employees')
          .update(updateData)
          .eq('id', initialData.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('employees')
          .insert([employeeData])
          .select()
          .single();
      }

      if (result.error) {
        if (result.error.code === '23505') {
          throw new Error('An employee with this email already exists');
        }
        if (result.error.code === '42501') {
          throw new Error('You do not have permission to perform this action');
        }
        throw new Error(result.error.message);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving employee:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving employee data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label htmlFor="employee-first-name" className="block text-sm font-medium text-gray-700">First Name</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="employee-first-name"
              required
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-last-name" className="block text-sm font-medium text-gray-700">Last Name</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="employee-last-name"
              required
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-email" className="block text-sm font-medium text-gray-700">Email</label>
          <div className="mt-1">
            <input
              type="email"
              id="employee-email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-position" className="block text-sm font-medium text-gray-700">Position</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="employee-position"
              required
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-department" className="block text-sm font-medium text-gray-700">Department</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="employee-department"
              required
              value={formData.department}
              onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-payment-type" className="block text-sm font-medium text-gray-700">Payment Type</label>
          <select
            id="employee-payment-type"
            required
            value={formData.paymentType}
            onChange={(e) => setFormData(prev => ({ ...prev, paymentType: e.target.value as 'hourly' | 'salary' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="salary">Salary</option>
            <option value="hourly">Hourly</option>
          </select>
        </div>

        <div>
          <label htmlFor="employee-salary" className="block text-sm font-medium text-gray-700">
            {formData.paymentType === 'salary' ? 'Annual Salary' : 'Hourly Rate'}
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="employee-salary"
              required
              min="0"
              step="0.01"
              value={formData.salary}
              onChange={(e) => setFormData(prev => ({ ...prev, salary: parseFloat(e.target.value) || 0 }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-bonus" className="block text-sm font-medium text-gray-700">Bonus Amount</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="employee-bonus"
              required
              min="0"
              step="0.01"
              value={formData.bonus}
              onChange={(e) => setFormData(prev => ({ ...prev, bonus: parseFloat(e.target.value) || 0 }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">One-time bonus to be paid in next payroll period</p>
        </div>

        <div>
          <label htmlFor="employee-start-date" className="block text-sm font-medium text-gray-700">Start Date</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              id="employee-start-date"
              required
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-tax-status" className="block text-sm font-medium text-gray-700">Tax Status</label>
          <select
            id="employee-tax-status"
            required
            value={formData.taxStatus}
            onChange={(e) => {
              const newTaxStatus = e.target.value;
              setFormData(prev => ({
                ...prev,
                taxStatus: newTaxStatus,
                federalTaxRate: newTaxStatus === '1099' ? 15 : 22
              }));
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="W2">W2</option>
            <option value="1099">1099</option>
          </select>
        </div>

        <div>
          <label htmlFor="employee-state" className="block text-sm font-medium text-gray-700">State</label>
          <select
            id="employee-state"
            required
            value={formData.state}
            onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">Select a state</option>
            {states.map(state => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="employee-state-rate" className="block text-sm font-medium text-gray-700">State Tax Amount ($)</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="employee-state-rate"
              required
              min="0"
              step="1"
              value={formData.stateRate}
              onChange={(e) => setFormData(prev => ({ ...prev, stateRate: parseInt(e.target.value) || 0 }))}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {formData.taxStatus === '1099' ? 'Not used for 1099 contractors' : 'Enter the fixed amount to be deducted each pay period'}
          </p>
        </div>

        <div>
          <label htmlFor="employee-federal-tax-rate" className="block text-sm font-medium text-gray-700">Federal Tax Rate (%)</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="employee-federal-tax-rate"
              required
              min="0"
              max="100"
              step="0.01"
              value={formData.federalTaxRate}
              onChange={(e) => {
                const value = e.target.value === '' ? 0 : Number(e.target.value);
                if (!isNaN(value) && value >= 0) {
                  setFormData(prev => ({ ...prev, federalTaxRate: value }));
                }
              }}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {formData.taxStatus === '1099' ? 'Not used for 1099 contractors (no taxes withheld)' : 'Standard rate is 22% for W2 employees'}
          </p>
        </div>

        <div>
          <label htmlFor="employee-insurance-rate" className="block text-sm font-medium text-gray-700">Insurance Rate (%)</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="employee-insurance-rate"
              required
              min="0"
              max="100"
              step="0.01"
              value={formData.insuranceRate}
              onChange={(e) => {
                const value = e.target.value === '' ? 0 : Number(e.target.value);
                setFormData(prev => ({ ...prev, insuranceRate: value }));
              }}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="employee-retirement-rate" className="block text-sm font-medium text-gray-700">Retirement Rate (%)</label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="employee-retirement-rate"
              required
              min="0"
              max="100"
              step="0.01"
              value={formData.retirementRate}
              onChange={(e) => {
                const value = e.target.value === '' ? 0 : Number(e.target.value);
                setFormData(prev => ({ ...prev, retirementRate: value }));
              }}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="employee-is-active"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="employee-is-active" className="ml-2 block text-sm text-gray-900">
              Active Employee
            </label>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Inactive employees will not appear in payroll processing but their data is preserved
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {loading ? 'Saving...' : initialData ? 'Update Employee' : 'Add Employee'}
        </button>
      </div>
    </form>
  );
};

export default EmployeeForm;